import type { Order } from "../../../domain/entities/Order";
import type { OrderItem } from "../../../domain/entities/OrderItem";
import type { OrderId } from "../../../domain/value-objects/OrderId";
import type { BookingCode } from "../../../domain/value-objects/BookingCode";
import { createBookingCode } from "../../../domain/value-objects/BookingCode";
import { createMoney } from "../../../domain/value-objects/Money";
import { createOrderId } from "../../../domain/value-objects/OrderId";
import type { OrderQueryPort } from "../../../application/ports/OrderQueryPort";

interface OrdersHttpAdapterConfig {
  baseUrl: string;
  authorizationToken: string;
  currency: string;
}

interface OrderEventHttp {
  event: string;
  date: string;
}

interface OrderHttpResponse {
  code: string;
  client_email: string;
  amount: number;
  type: string;
  created_at: string;
  status: string;
  events_history: OrderEventHttp[];
  product_order?: {
    reservation_expires_at: string;
    lines: Array<{
      id: string;
      product_title: string;
      primary_image?: string;
      variant_options: Record<string, string>;
      quantity: number;
      subtotal: { amount_minor: number; currency: string };
    }>;
  };
  pricing_summary?: {
    gross_total_minor: number;
    eligible_subtotal_minor: number;
    non_discountable_minor: number;
    discount_minor: number;
    net_total_minor: number;
    coupon_code?: string;
    subsidy_minor: number;
  };
}

interface BookingItemHttp {
  code: string;
  name: string;
  price: number;
}

interface BookingHttp {
  order_code: string;
  code?: string;
  items: BookingItemHttp[];
}

interface BookingListHttpResponse {
  items: BookingHttp[];
}

interface BffResponse<T> {
  success: boolean;
  data: T;
}

const ORDERS_PATH = "orders";
const BOOKINGS_PATH = "bookings";

export class OrdersHttpAdapter implements OrderQueryPort {
  constructor(private readonly config: OrdersHttpAdapterConfig) {}

  async getById(orderId: OrderId): Promise<Order> {
    const orderResponse = await this.fetchOrder(orderId);
    const bookingDetails =
      orderResponse.type === "SERVICE" ? await this.fetchBookingDetails(orderId) : null;
    const productItems: OrderItem[] = (orderResponse.product_order?.lines ?? []).map(
      (line) => ({
        code: line.id,
        name: line.product_title,
        price: createMoney(line.subtotal.amount_minor / 100, line.subtotal.currency),
        quantity: line.quantity,
        imageUrl: line.primary_image,
        variantOptions: line.variant_options,
      }),
    );

    return {
      id: createOrderId(orderResponse.code),
      code: orderResponse.code,
      clientEmail: orderResponse.client_email,
      amount: createMoney(orderResponse.amount, this.config.currency),
      type: orderResponse.type === "SERVICE" ? "SERVICE" : "PRODUCT",
      status: orderResponse.status,
      createdAt: new Date(orderResponse.created_at),
      eventsHistory: (orderResponse.events_history ?? []).map((event) => ({
        event: event.event,
        occurredAt: new Date(event.date),
      })),
      bookingCode: bookingDetails?.bookingCode,
      items: orderResponse.type === "PRODUCT" ? productItems : bookingDetails?.items ?? [],
      paymentExpiresAt: orderResponse.product_order?.reservation_expires_at
        ? new Date(orderResponse.product_order.reservation_expires_at)
        : undefined,
      pricing: orderResponse.pricing_summary
        ? {
            grossTotalMinor: orderResponse.pricing_summary.gross_total_minor,
            eligibleSubtotalMinor: orderResponse.pricing_summary.eligible_subtotal_minor,
            nonDiscountableMinor: orderResponse.pricing_summary.non_discountable_minor,
            discountMinor: orderResponse.pricing_summary.discount_minor,
            netTotalMinor: orderResponse.pricing_summary.net_total_minor,
            couponCode: orderResponse.pricing_summary.coupon_code,
            subsidyMinor: orderResponse.pricing_summary.subsidy_minor,
          }
        : undefined,
    };
  }

  async applyCoupon(orderId: OrderId, code: string): Promise<Order> {
    await this.mutateCoupon(orderId, "POST", { code });
    return this.getById(orderId);
  }

  async removeCoupon(orderId: OrderId): Promise<Order> {
    await this.mutateCoupon(orderId, "DELETE");
    return this.getById(orderId);
  }

  private async mutateCoupon(
    orderId: OrderId,
    method: "POST" | "DELETE",
    body?: { code: string },
  ): Promise<void> {
    const response = await fetch(
      this.composeUrl(`${ORDERS_PATH}/${encodeURIComponent(orderId)}/coupon`),
      {
        method,
        headers: {
          ...this.buildHeaders(),
          ...(body ? { "Content-Type": "application/json" } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
      },
    );
    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as { message?: string } | null;
      throw new Error(payload?.message ?? "No se pudo aplicar el código promocional");
    }
  }

  private async fetchOrder(orderId: OrderId): Promise<OrderHttpResponse> {
    const url = this.composeUrl(
      `${ORDERS_PATH}/${encodeURIComponent(orderId)}`,
    );
    const response = await fetch(url, {
      headers: this.buildHeaders(),
    });

    if (!response.ok) {
      throw new Error("No fue posible obtener la información de la orden");
    }

    const payload = (await response.json()) as
      | BffResponse<OrderHttpResponse>
      | OrderHttpResponse;
    return this.unwrapBffData(payload);
  }

  private async fetchBookingDetails(
    orderId: OrderId,
  ): Promise<{ bookingCode?: BookingCode; items: OrderItem[] } | null> {
    const url = this.composeUrl(BOOKINGS_PATH, { order_code: orderId, limit: "1" });
    const response = await fetch(url, {
      headers: this.buildHeaders(),
    });

    if (!response.ok) {
      // When the booking is not found we return an empty list instead of failing the entire flow.
      if (response.status === 404) {
        return null;
      }

      throw new Error("No fue posible obtener los detalles de la reserva");
    }

    const payload = (await response.json()) as
      | BffResponse<BookingHttp[]>
      | BookingListHttpResponse;
    const bookingsPayload = this.unwrapBffData<
      BookingHttp[] | BookingListHttpResponse
    >(payload);
    const bookings = Array.isArray(bookingsPayload)
      ? bookingsPayload
      : bookingsPayload.items ?? [];
    const [booking] = bookings;

    if (!booking) {
      return null;
    }

    const bookingItems = (booking.items ?? []).map((item) => ({
      code: item.code,
      name: item.name,
      price: createMoney(item.price, this.config.currency),
    }));

    const bookingCode = booking.code ? createBookingCode(booking.code) : undefined;

    return {
      bookingCode,
      items: bookingItems,
    };
  }

  private composeUrl(path: string, params?: Record<string, string>): string {
    const normalizedBase = this.config.baseUrl.endsWith("/")
      ? this.config.baseUrl
      : `${this.config.baseUrl}/`;
    const url = new URL(path, normalizedBase);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    return url.toString();
  }

  private unwrapBffData<T>(payload: T | BffResponse<T>): T {
    if (this.isBffResponse(payload)) {
      return payload.data;
    }

    return payload;
  }

  private isBffResponse<T>(payload: T | BffResponse<T>): payload is BffResponse<T> {
    return (
      typeof payload === "object" &&
      payload !== null &&
      "success" in payload &&
      "data" in payload
    );
  }

  private buildHeaders(): HeadersInit {
    return {
      Accept: "application/json",
      Authorization: `${this.config.authorizationToken}`,
    };
  }
}
